import {createLogger} from "@gongt/ts-stl-library/log/debug";
import {LOG_LEVEL} from "@gongt/ts-stl-library/log/levels";
import {ICacheClassPromise} from "@gongt/ts-stl-server/database/cache";
import {inspect} from "util";
import {IUserProfile, IUserProfileBase} from "../bridge/define";
import {memoryCache} from "../inc/cache";
import {ClientJsBuilder} from "../inc/client-js-builder";
import {ExpressSupport} from "../inc/express-support";
import {PermissionSystem} from "../inc/permission-system";
import {UserSystem} from "../inc/user-system";
import {tryGetBaseUrl} from "./try-get-url";
import {UserCenterApi} from "./user-center-api";

const debug = createLogger(LOG_LEVEL.INFO, 'package');

export type LoginParams = LoginParamsPassword|LoginParamsToken;

export interface LoginParamsPassword {
	login: string;
	password: string;
}

export interface LoginParamsToken {
	token: string;
}

export interface IUserCenterOptions {
	clientUseHttps?: boolean;
	projectName: string;
	serverRequestKey: string;
	useCookie: boolean;
	/** 替换默认的内存缓存 */
	cacheObject: ICacheClassPromise;
	serverKey: string;
	userCenterBaseUrl: string;
}

export class UserCenter<IProfile extends IUserProfileBase = IUserProfile> {
	public readonly options: IUserCenterOptions;
	
	/** @internal */
	public readonly cache: ICacheClassPromise;
	
	public readonly express: ExpressSupport<IProfile>;
	public readonly api: UserCenterApi<IProfile>;
	public readonly client: ClientJsBuilder<IProfile>;
	public readonly permission: PermissionSystem<IProfile>;
	public readonly user: UserSystem<IProfile>;
	
	public readonly projectName: string;
	public readonly ucBaseUrl: string;
	
	public get serverKey(): string {
		return this.options.serverKey;
	}
	
	constructor(options: Partial<IUserCenterOptions> = {}) {
		if (!options) {
			throw new Error('new UserCenter() require a options');
		}
		options = this.options = <any>Object.assign({}, options);
		debug('construct package with %j', options);
		
		if (options.cacheObject) {
			this.cache = options.cacheObject;
		} else {
			this.cache = options.cacheObject = memoryCache;
		}
		if (!options.projectName) {
			if (!process.env.PROJECT_NAME) {
				throw new Error('new UserCenter() require a `projectName` option, or a PROJECT_NAME environment.');
			}
			options.projectName = process.env.PROJECT_NAME;
		}
		
		if (options.userCenterBaseUrl) {
			this.ucBaseUrl = options.userCenterBaseUrl;
			if (!/^http:/.test(this.ucBaseUrl)) {
				throw new TypeError('new UserCenter() `userCenterBaseUrl` option must start with http://, not https, or anything else.')
			}
		} else {
			tryGetBaseUrl(options);
			this.ucBaseUrl = options.userCenterBaseUrl;
			if (!this.ucBaseUrl) {
				throw new Error('new UserCenter() require a `userCenterBaseUrl` option, or use JsonEnv.');
			}
		}
		
		if (!options.serverKey) {
			options.serverKey = (() => {
				try {
					const {JsonEnv} = require('@gongt/jenv-data');
					return JsonEnv.serverRequestKey;
				} catch (e) {
				}
			})();
		}
		if (!options.serverKey) {
			throw new Error('new UserCenter() require a `serverKey` option. or you can use JsonEnv.');
		}
		
		this.express = new ExpressSupport(this);
		this.api = new UserCenterApi(this);
		this.client = new ClientJsBuilder(this);
		this.permission = new PermissionSystem(this);
		this.user = new UserSystem(this);
		this.projectName = options.projectName;
		debug('    auto detected options: %j', options);
	}
	
	/** @deprecated */
	createPermission(...args: any[]): any {
		return this.permission.createClass.apply(this.permission, args);
	}
	
	/** @deprecated */
	registerPermission(...args: any[]): any {
		return this.permission.registerClass.apply(this.permission, args);
	}
	
	/** @deprecated */
	public get AbstractPermission(): any {
		return this.permission.AbstractClass;
	}
	
	/** @deprecated */
	empty(...args: any[]): any {
		return this.user.createEmpty.apply(this.user, args);
	}
	
	/** @deprecated */
	mapUserId(...args: any[]): any {
		return this.user.mapEmailToId.apply(this.user, args);
	}
	
	/** @deprecated */
	getUserProfileByEmail(...args: any[]): any {
		return this.user.profileByEmail.apply(this.user, args);
	}
	
	/** @deprecated */
	getUserProfile(...args: any[]): any {
		return this.user.profile.apply(this.user, args);
	}
	
	/** @deprecated */
	login(...args: any[]): any {
		return this.user.login.apply(this.user, args);
	}
	
	updateCache(token: string): Promise<void> {
		return this.cache.del(['token', token]);
	}
	
	toJSON() {
		return {
			name: this.constructor.name,
		};
	}
	
	toString() {
		return this.constructor.name + '.toString()';
	}
	
	inspect(depth, options) {
		if (depth < 0) {
			return options.stylize('[Box]', 'special');
		}
		const newOptions = Object.assign({}, options, {
			depth: options.depth === null? null : options.depth - 1,
		});
		const {cacheObject, ...opt} = this.options;
		
		const padding = ' '.repeat(this.constructor.name.length + 1);
		return this.constructor.name + ' ' + inspect(opt, newOptions).replace(/\n/g, `\n${padding}`);
	}
}
